import React from 'react';
import { useI18n } from '../../i18n/I18n';
import { PaymentSession } from '../../views/admin/interfaces';

export interface POSSessionCardProps {
    session: PaymentSession;
    isExpanded: boolean;
    onToggleExpand?: () => void;
    onOpenQrModal?: (e: React.MouseEvent, session: PaymentSession) => void;
    onCopyLink: () => void;
    onDownloadPDF: () => void;
    onWelcomeCard: () => void;
    shouldShowWelcomeCard: boolean;
    hideChevron?: boolean;
}

const formatDate = (dateArray: any) => {
    if (!dateArray || !Array.isArray(dateArray)) return '';
    try {
        const date = new Date(
            parseInt(dateArray[0]),
            parseInt(dateArray[1]) - 1,
            parseInt(dateArray[2]),
            parseInt(dateArray[3] || '0'),
            parseInt(dateArray[4] || '0')
        );
        return date.toLocaleString(undefined, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (e) {
        return '';
    }
};

export default function POSSessionCard({
    session,
    isExpanded,
    onToggleExpand,
    onOpenQrModal,
    onCopyLink,
    onDownloadPDF,
    onWelcomeCard,
    shouldShowWelcomeCard,
    hideChevron = false
}: POSSessionCardProps) {
    const { t } = useI18n();

    return (
        <div className="glass-panel rounded-2xl p-4 transition-all duration-300 hover:bg-surface-hover group" style={{ borderColor: 'var(--color-surface-border)' }}>
            <div
                className={onToggleExpand ? "cursor-pointer" : ""}
                onClick={onToggleExpand}
            >
                {/* Row 1: QR icon + name + badge + chevron */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={(e) => {
                            if (onOpenQrModal) {
                                e.stopPropagation();
                                onOpenQrModal(e, session);
                            }
                        }}
                        className="w-9 h-9 rounded-xl bg-deep-navy flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer hover:border-electric-blue/50 hover:shadow-[0_0_12px_rgba(19,127,236,0.25)] transition-all duration-300 group/qr"
                        style={{ border: '1px solid var(--color-surface-border)' }}
                        title="Ver código QR"
                    >
                        <span className="material-symbols-outlined text-electric-blue text-[18px] group-hover/qr:scale-110 transition-transform">qr_code_2</span>
                    </button>
                    <h3 className="flex-1 min-w-0 text-txt-heading font-bold text-[15px] truncate leading-tight">
                        {session.name || session.concept || session.template || 'Payment Link'}
                    </h3>
                    <div className="bg-accent-purple/20 text-accent-purple text-[10px] font-bold px-2.5 py-1 rounded-lg border border-accent-purple/20 tracking-wide uppercase flex-shrink-0">
                        {session.currency === 'ARS' 
                            ? `ARS ${session.exchangeRateType === 'CRYPTO' ? 'Crypto' : 'Blue'}${session.exchangeRateAdjustment ? ` ${session.exchangeRateAdjustment > 0 ? '+' : ''}${session.exchangeRateAdjustment}%` : ''}`
                            : session.currency || ''}
                    </div>
                    {!hideChevron && (
                        <span className={`material-symbols-outlined text-txt-muted flex-shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                            expand_more
                        </span>
                    )}
                </div>
                {/* Row 2: date */}
                <div className="flex items-center mt-2 pl-12">
                    <p className="text-txt-muted text-[11px]">
                        {formatDate(session.createdDate)}
                    </p>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="mt-6 pt-5" style={{ borderTop: '1px solid var(--divider)' }}>
                    <div className="grid grid-cols-3 gap-2">
                        <button
                            onClick={(e) => { e.stopPropagation(); onCopyLink(); }}
                            className="group/btn flex flex-col items-center bg-transparent border-none cursor-pointer"
                        >
                            <div className="w-11 h-11 rounded-full flex items-center justify-center mb-1.5 transition-colors group-hover/btn:bg-electric-blue/20 group-hover/btn:text-electric-blue text-txt-secondary" style={{ background: 'var(--color-surface)' }}>
                                <span className="material-symbols-outlined text-[20px]">link</span>
                            </div>
                            <span className="text-[9px] font-semibold text-txt-muted uppercase tracking-tighter">{t('pos.paymentLink')}</span>
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onDownloadPDF(); }}
                            className="group/btn flex flex-col items-center bg-transparent border-none cursor-pointer"
                        >
                            <div className="w-11 h-11 rounded-full flex items-center justify-center mb-1.5 transition-colors group-hover/btn:bg-electric-blue/20 group-hover/btn:text-electric-blue text-txt-secondary" style={{ background: 'var(--color-surface)' }}>
                                <span className="material-symbols-outlined text-[20px]">picture_as_pdf</span>
                            </div>
                            <span className="text-[9px] font-semibold text-txt-muted uppercase tracking-tighter">{t('pos.downloadPdf')}</span>
                        </button>
                        <button
                            onClick={(e) => { e.stopPropagation(); onWelcomeCard(); }}
                            disabled={!shouldShowWelcomeCard}
                            className={`group/btn flex flex-col items-center bg-transparent border-none cursor-pointer ${!shouldShowWelcomeCard ? 'opacity-30 grayscale cursor-not-allowed' : ''}`}
                        >
                            <div className="w-11 h-11 rounded-full flex items-center justify-center mb-1.5 transition-colors group-hover/btn:bg-electric-blue/20 group-hover/btn:text-electric-blue text-txt-secondary" style={{ background: 'var(--color-surface)' }}>
                                <span className="material-symbols-outlined text-[20px]">badge</span>
                            </div>
                            <span className="text-[9px] font-semibold text-txt-muted uppercase tracking-tighter">{t('pos.welcomeCard')}</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
